import type { CausalEdge, GameState, IndicatorKey, SimulationEvent } from '../../types/simulation.js';
import { MAX_CAUSAL_DEPTH } from '../../config/game.js';
import { DeterministicRandom } from './Random.js';

const finite = (value: number, fallback = 0) => Number.isFinite(value) ? value : fallback;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export class CausalityEngine {
  constructor(private readonly edges: CausalEdge[]) {}

  applyIndicatorDelta(
    state: GameState,
    key: IndicatorKey,
    requestedDelta: number,
    reason: string,
    eventId: string | undefined,
    path: IndicatorKey[],
    rng: DeterministicRandom
  ): SimulationEvent[] {
    const indicator = state.indicators[key];
    if (!indicator || !Number.isFinite(requestedDelta)) return [];

    const inertia = 0.82;
    const appliedDelta = finite(requestedDelta) * inertia;
    const previousValue = finite(indicator.value);
    const newValue = clamp(previousValue + appliedDelta, indicator.min, indicator.max);
    const actualDelta = newValue - previousValue;

    indicator.value = newValue;
    indicator.updatedAt = state.clock.currentTime;

    state.journal.push({
      id: `journal-${state.worldVersion}-${state.journal.length + 1}`,
      at: state.clock.currentTime,
      source: eventId ?? 'moteur',
      reason,
      variable: key,
      previousValue,
      requestedDelta,
      appliedDelta: actualDelta,
      newValue,
      eventId,
      path: [...path, key]
    });

    if (Math.abs(actualDelta) < 0.0001 || path.length >= MAX_CAUSAL_DEPTH) return [];

    const nextPath = [...path, key];
    const consequences: SimulationEvent[] = [];
    for (const edge of this.edges.filter((candidate) => candidate.source === key)) {
      if (nextPath.includes(edge.target)) continue; // coupe les boucles A→B→A.
      if (edge.threshold !== undefined && Math.abs(actualDelta) < edge.threshold) continue;
      const uncertaintyFactor = 1 + rng.normalish() * edge.uncertainty;
      const rawDelta = actualDelta * edge.coefficient * uncertaintyFactor;
      const delta = clamp(rawDelta, -edge.maxAbsoluteDelta, edge.maxAbsoluteDelta);
      if (Math.abs(delta) < 0.0001) continue;
      const at = new Date(Date.parse(state.clock.currentTime) + edge.delayHours * 3600000).toISOString();
      consequences.push({
        id: `causal-${edge.id}-${state.worldVersion}-${consequences.length}`,
        type: 'causal.effect',
        title: `Effet différé : ${state.indicators[edge.target].label}`,
        description: `Conséquence planifiée depuis ${indicator.label}; l'effet reste soumis à l'incertitude et aux limites du modèle.`,
        category: 'systeme',
        severity: 'information',
        scheduledAt: at,
        createdAt: state.clock.currentTime,
        source: `Causalité:${edge.id}`,
        confidence: Math.max(0.05, 1 - edge.uncertainty),
        processed: false,
        parentEventId: eventId,
        payload: { key: edge.target, delta, path: nextPath, reason: `${indicator.label} → ${state.indicators[edge.target].label}` }
      });
    }
    return consequences;
  }
}
